"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const bus_rdf_resolve_quad_pattern_1 = require("@comunica/bus-rdf-resolve-quad-pattern");
const data_factory_1 = require("@comunica/data-factory");
const DataFactory = require("@rdfjs/data-model");
const asynciterator_1 = require("asynciterator");
const asynciterator_promiseproxy_1 = require("asynciterator-promiseproxy");
const asynciterator_union_1 = require("asynciterator-union");
const rdf_terms_1 = require("rdf-terms");
const sparqlalgebrajs_1 = require("sparqlalgebrajs");
/**
 * A FederatedQuadSource can evaluate quad pattern queries over the union of different heterogeneous sources.
 * It will call the given mediator to evaluate each quad pattern query separately.
 */
class FederatedQuadSource {
    constructor(mediatorResolveQuadPattern, context, emptyPatterns, skipEmptyPatterns) {
        this.mediatorResolveQuadPattern = mediatorResolveQuadPattern;
        this.sources = context.get(bus_rdf_resolve_quad_pattern_1.KEY_CONTEXT_SOURCES);
        this.contextDefault = context.delete(bus_rdf_resolve_quad_pattern_1.KEY_CONTEXT_SOURCES);
        this.emptyPatterns = emptyPatterns;
        this.sourceIds = new Map();
        this.skipEmptyPatterns = skipEmptyPatterns;
        this.algebraFactory = new sparqlalgebrajs_1.Factory();
        // Initialize sources in the emptyPatterns datastructure
        if (this.skipEmptyPatterns) {
            this.sources.iterator().on('data', (source) => {
                if (!this.emptyPatterns.has(source)) {
                    this.emptyPatterns.set(source, []);
                }
            });
        }
    }
    /**
     * Check if the given RDF term is not bound to an exact value.
     * I.e., if it is not a Variable.
     * @param {RDF.Term} term An RDF term.
     * @return {boolean} If it is not bound.
     */
    static isTermBound(term) {
        return term.termType !== 'Variable';
    }
    /**
     * Checks if the given (child) pattern is a more bound version of the given (parent) pattern.
     * This will also return true if the patterns are equal.
     * @param {RDF.BaseQuad} child A child pattern.
     * @param {RDF.BaseQuad} parent A parent pattern.
     * @return {boolean} If child is a sub-pattern of parent
     */
    static isSubPatternOf(child, parent) {
        return (!FederatedQuadSource.isTermBound(parent.subject) || parent.subject.equals(child.subject))
            && (!FederatedQuadSource.isTermBound(parent.predicate) || parent.predicate.equals(child.predicate))
            && (!FederatedQuadSource.isTermBound(parent.object) || parent.object.equals(child.object))
            && (!FederatedQuadSource.isTermBound(parent.graph) || parent.graph.equals(child.graph));
    }
    /**
     * If the given term is a blank node, return a deterministic named node for it
     * based on the source id and the blank node value.
     * @param term Any RDF term.
     * @param sourceId A source identifier.
     * @return If the given term was a blank node, this will return a skolemized named node, otherwise the original term.
     */
    static skolemizeTerm(term, sourceId) {
        if (term.termType === 'BlankNode') {
            return new data_factory_1.BlankNodeScoped(`bc_${sourceId}_${term.value}`, DataFactory.namedNode(`${FederatedQuadSource.SKOLEM_PREFIX}${sourceId}:${term.value}`));
        }
        return term;
    }
    /**
     * Skolemize all terms in the given quad.
     * @param quad An RDF quad.
     * @param sourceId A source identifier.
     * @return The skolemized quad.
     */
    static skolemizeQuad(quad, sourceId) {
        return rdf_terms_1.mapTerms(quad, (term) => FederatedQuadSource.skolemizeTerm(term, sourceId));
    }
    /**
     * If a given term was a skolemized named node for the given source id,
     * deskolemize it again to a blank node.
     * If the given term was a skolemized named node for another source, return false.
     * If the given term was not a skolemized named node, return the original term.
     * @param term Any RDF term.
     * @param sourceId A source identifier.
     */
    static deskolemizeTerm(term, sourceId) {
        if (term.termType === 'BlankNode' && 'skolemized' in term) {
            term = term.skolemized;
        }
        if (term.termType === 'NamedNode') {
            if (term.value.startsWith(FederatedQuadSource.SKOLEM_PREFIX)) {
                const colonSeparator = term.value.indexOf(':', FederatedQuadSource.SKOLEM_PREFIX.length);
                const termSourceId = term.value.substr(FederatedQuadSource.SKOLEM_PREFIX.length, colonSeparator - FederatedQuadSource.SKOLEM_PREFIX.length);
                // We had a skolemized term
                if (termSourceId === sourceId) {
                    // It came from the correct source
                    const termLabel = term.value.substr(colonSeparator + 1, term.value.length);
                    return DataFactory.blankNode(termLabel);
                }
                else {
                    // It came from a different source
                    return null;
                }
            }
        }
        return term;
    }
    /**
     * If the given source is guaranteed to produce an empty result for the given pattern.
     *
     * This prediction is done based on the 'emptyPatterns' datastructure that is stored within this actor.
     * Every time an empty pattern is passed, this pattern is stored in this datastructure for this source.
     * If this pattern (or a more bound pattern) is queried, we know for certain that it will be empty again.
     * This is under the assumption that sources will remain static during query evaluation.
     *
     * @param {IQuerySource} source
     * @param {RDF.BaseQuad} pattern
     * @return {boolean}
     */
    isSourceEmpty(source, pattern) {
        if (!this.skipEmptyPatterns) {
            return false;
        }
        const emptyPatterns = this.emptyPatterns.get(source);
        if (emptyPatterns) {
            for (const emptyPattern of emptyPatterns) {
                if (FederatedQuadSource.isSubPatternOf(pattern, emptyPattern)) {
                    return true;
                }
            }
        }
        return false;
    }
    /**
     * Get the unique, deterministic id for the given source.
     * @param source A data source.
     * @return The id of the given source.
     */
    getSourceId(source) {
        let sourceId = this.sourceIds.get(source);
        if (sourceId === undefined) {
            sourceId = `${this.sourceIds.size}`;
            this.sourceIds.set(source, sourceId);
        }
        return sourceId;
    }
    matchLazy(subject, predicate, object, graph) {
        if (subject instanceof RegExp
            || predicate instanceof RegExp
            || object instanceof RegExp
            || graph instanceof RegExp) {
            throw new Error("FederatedQuadSource does not support matching by regular expressions.");
        }
        // Counters for our metadata
        const metadata = { totalItems: 0 };
        let remainingSources = 1;
        let sourcesCount = 0;
        // Anonymous function to handle totalItems from metadata
        const checkEmitMetadata = (currentTotalItems, source, pattern, lastMetadata) => {
            if (this.skipEmptyPatterns && !currentTotalItems) {
                // Because another call may have added more information in the meantime
                if (!this.isSourceEmpty(source, pattern)) {
                    this.emptyPatterns.get(source).push(pattern);
                }
            }
            if (!remainingSources) {
                if (lastMetadata && sourcesCount === 1) {
                    // If we only had one source, emit the metadata as-is.
                    it.emit('metadata', lastMetadata);
                }
                else {
                    it.emit('metadata', metadata);
                }
            }
        };
        // TODO: A solution without cloning would be preferred here.
        //       See discussion at https://github.com/comunica/comunica/issues/553
        const sourcesIt = this.sources.iterator();
        const proxyIt = sourcesIt.map((source) => {
            const sourceId = this.getSourceId(source);
            remainingSources++;
            sourcesCount++;
            return new asynciterator_promiseproxy_1.PromiseProxyIterator(async () => {
                // Convert falsy terms to variables, reverse operation of {@link ActorRdfResolveQuadPatternSource#variableToNull}.
                // Deskolemize terms, so we send the original blank nodes to each source.
                // Note that some sources may not match bnodes by label. SPARQL endpoints for example consider them variables.
                const s = !subject ? DataFactory.variable('vs') : FederatedQuadSource.deskolemizeTerm(subject, sourceId);
                const p = !predicate ? DataFactory.variable('vp') : FederatedQuadSource.deskolemizeTerm(predicate, sourceId);
                const o = !object ? DataFactory.variable('vo') : FederatedQuadSource.deskolemizeTerm(object, sourceId);
                const g = !graph ? DataFactory.variable('vg') : FederatedQuadSource.deskolemizeTerm(graph, sourceId);
                let pattern;
                // Prepare the context for this specific source
                const context = this.contextDefault.set(bus_rdf_resolve_quad_pattern_1.KEY_CONTEXT_SOURCE, { type: bus_rdf_resolve_quad_pattern_1.getDataSourceType(source), value: bus_rdf_resolve_quad_pattern_1.getDataSourceValue(source) });
                let output;
                // If any of the deskolemized blank nodes originate from another source,
                // or if we can predict that the given source will have no bindings for the given pattern,
                // return an empty iterator.
                if (!s || !p || !o || !g
                    || this.isSourceEmpty(source, pattern = this.algebraFactory.createPattern(s, p, o, g))) {
                    output = { data: new asynciterator_1.EmptyIterator(), metadata: () => Promise.resolve({ totalItems: 0 }) };
                }
                else {
                    output = await this.mediatorResolveQuadPattern.mediate({ pattern, context });
                }
                if (output.metadata) {
                    output.metadata().then((subMetadata) => {
                        if ((!subMetadata.totalItems && subMetadata.totalItems !== 0) || !isFinite(subMetadata.totalItems)) {
                            metadata.totalItems = Infinity;
                            remainingSources = 0; // We're already at infinite, so ignore any later metadata
                            checkEmitMetadata(Infinity, source, pattern, subMetadata);
                        }
                        else {
                            metadata.totalItems += subMetadata.totalItems;
                            remainingSources--;
                            checkEmitMetadata(subMetadata.totalItems, source, pattern, subMetadata);
                        }
                    });
                }
                else {
                    metadata.totalItems = Infinity;
                    remainingSources = 0; // We're already at infinite, so ignore any later metadata
                    checkEmitMetadata(Infinity, source, pattern);
                }
                return output.data.map((quad) => FederatedQuadSource.skolemizeQuad(quad, sourceId));
            });
        });
        const it = new asynciterator_union_1.RoundRobinUnionIterator(proxyIt.clone());
        it.on('newListener', (eventName) => {
            if (eventName === 'metadata') {
                setImmediate(() => proxyIt.clone().each((proxy) => proxy.loadSource()));
            }
        });
        // If we have 0 sources, immediately emit metadata
        sourcesIt.on('end', () => {
            if (!--remainingSources) {
                it.emit('metadata', metadata);
            }
        });
        return it;
    }
    match(subject, predicate, object, graph) {
        return this.matchLazy(subject, predicate, object, graph);
    }
}
exports.FederatedQuadSource = FederatedQuadSource;
FederatedQuadSource.SKOLEM_PREFIX = 'urn:comunica_skolem:source_';
//# sourceMappingURL=FederatedQuadSource.js.map